import {
  deleteByScope as deleteExternalByScope,
  deleteOne as deleteExternalOne,
  getSettings as getExternalSettings,
  getUnreadCount as getExternalUnreadCount,
  list as getExternalList,
  markAllRead as markExternalAllRead,
  markRead as markExternalNotificationRead,
  updateSettings as updateExternalSettings,
} from "@synthesis/shared-services/notification/external/client";
import {
  getSettingsQueryKey as getExternalSettingsQueryKey,
  getUnreadCountQueryKey as getExternalUnreadCountQueryKey,
  listQueryKey as getExternalListQueryKey,
} from "@synthesis/shared-services/notification/external/hooks";
import type {
  CursorPageDtoNotificationDto,
  DeleteByScopeScopeKey,
  ListQuery,
  NotificationSettingsDto,
  UnreadCountDto,
} from "@synthesis/shared-services/notification/external/types";
import {
  deleteByScope as deleteInternalByScope,
  deleteOne as deleteInternalOne,
  getSettings as getInternalSettings,
  getUnreadCount as getInternalUnreadCount,
  list as getInternalList,
  markAllRead as markInternalAllRead,
  markRead as markInternalNotificationRead,
  updateSettings as updateInternalSettings,
} from "@synthesis/shared-services/notification/internal/client";
import {
  getSettingsQueryKey as getInternalSettingsQueryKey,
  getUnreadCountQueryKey as getInternalUnreadCountQueryKey,
  listQueryKey as getInternalListQueryKey,
} from "@synthesis/shared-services/notification/internal/hooks";

import type { NotificationClientType } from "./notifications-panel.lib";

export const getNotificationListQueryKey = (clientType: NotificationClientType | null, userId?: string) => {
  const baseKey = clientType === "internal" ? getInternalListQueryKey() : getExternalListQueryKey();

  return [...baseKey, clientType, userId] as const;
};

export const getNotificationUnreadCountQueryKey = (clientType: NotificationClientType | null, userId?: string) => {
  const baseKey = clientType === "internal" ? getInternalUnreadCountQueryKey() : getExternalUnreadCountQueryKey();

  return [...baseKey, clientType, userId] as const;
};

export const getNotificationSettingsQueryKey = (clientType: NotificationClientType | null, userId?: string) => {
  const baseKey = clientType === "internal" ? getInternalSettingsQueryKey() : getExternalSettingsQueryKey();

  return [...baseKey, clientType, userId] as const;
};

export const getNotifications = (
  clientType: NotificationClientType,
  params: ListQuery,
  config?: { signal?: AbortSignal },
): Promise<CursorPageDtoNotificationDto> => {
  const request = clientType === "internal" ? getInternalList : getExternalList;

  return request({ query: params, signal: config?.signal }).then(({ data }) => data);
};

export const getUnreadNotificationsCount = (clientType: NotificationClientType, config?: { signal?: AbortSignal }): Promise<UnreadCountDto> => {
  const request = clientType === "internal" ? getInternalUnreadCount : getExternalUnreadCount;

  return request({ signal: config?.signal }).then(({ data }) => data);
};

export const getNotificationSettings = (clientType: NotificationClientType, config?: { signal?: AbortSignal }): Promise<NotificationSettingsDto> => {
  const request = clientType === "internal" ? getInternalSettings : getExternalSettings;

  return request({ signal: config?.signal }).then(({ data }) => data);
};

export const getMarkReadFn = (clientType: NotificationClientType) =>
  clientType === "internal" ? markInternalNotificationRead : markExternalNotificationRead;

export const getMarkAllReadFn = (clientType: NotificationClientType) => (clientType === "internal" ? markInternalAllRead : markExternalAllRead);

export const getDeleteOneFn = (clientType: NotificationClientType) => (clientType === "internal" ? deleteInternalOne : deleteExternalOne);

export const getDeleteByScopeFn = (clientType: NotificationClientType) => {
  const request = clientType === "internal" ? deleteInternalByScope : deleteExternalByScope;

  return (scope: DeleteByScopeScopeKey, config?: { signal?: AbortSignal }) => request({ query: { scope }, signal: config?.signal });
};

export const getUpdateSettingsFn = (clientType: NotificationClientType) =>
  clientType === "internal" ? updateInternalSettings : updateExternalSettings;
